import { Takeover } from '@sch-inventory/advertory/sites/aftenposten/index.js';
import React, { useEffect, useRef } from 'react';

import type {
  ByfunnFrapiArticle,
  GodtTeasers,
  Recommendations,
} from '../../../types/frapi-types.js';
import type { OptionalDataFailed } from '../../../../core/fetching/index.js';
import type { SiteVersion } from '../../../../core/system/rendering/types.js';
import type { ArticleProps } from '../../components/articles/types.js';

import { ArticleType } from './ArticleType.js';
import { DataController } from '../../components/commonComponents/DataController.js';
import { withByfunnPulse, trackArticlePageView } from '../../../pulse/index.js';
import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import { shouldDisplayAds } from '../../components/articles/helpers.js';
import { insertAdsPlacements } from '../../components/ads/Ad/helpers.js';
import { useSessionData } from '../../../../../public-src/core/js/schibsted-account/react.server.js';
import { ListsProvider } from '../../components/saving/ListsProvider.js';
import useInitializeAds from '../../hooks/useInitAds.js';
import { useAdjustAdvertoryTakeoverStyles } from '../../hooks/useAdjustAdvertoryTakeoverStyles.js';

type ArticlePageProps = {
  article: ByfunnFrapiArticle;
  recommendations: Recommendations | OptionalDataFailed;
  godtTeasers: GodtTeasers | OptionalDataFailed;
  isMobile?: boolean;
  siteVersion: SiteVersion;
  isHermesApp?: boolean;
};

const ArticlePage: React.FC<ArticlePageProps> = ({
  article,
  recommendations,
  godtTeasers,
  isMobile = false,
  siteVersion,
  isHermesApp,
}) => {
  const { registerPageLeaveTracking, getEnvironmentId, trackViewArticle } =
    usePulseContext();
  const session = useSessionData();
  const articleElementRef = useRef(null);

  useEffect(() => {
    trackArticlePageView(trackViewArticle, article);
  }, [trackViewArticle, article]);

  useEffect(() => {
    if (articleElementRef.current) {
      registerPageLeaveTracking({
        objectElement: articleElementRef.current,
      });
    }
  }, [registerPageLeaveTracking]);

  useAdjustAdvertoryTakeoverStyles();

  useInitializeAds(getEnvironmentId, session, siteVersion);

  const componentsWithAds = shouldDisplayAds(article)
    ? insertAdsPlacements(
        isMobile,
        article.components,
        'article',
        article.section,
      )
    : article.components;

  const commonProps: ArticleProps = {
    article: { ...article, components: componentsWithAds },
    isMobile,
    recommendations,
    godtTeasers,
    isHermesApp,
    forwardedRef: articleElementRef,
  };

  return (
    <ListsProvider>
      <Takeover placementId="takeover" />
      <div
        className={`${siteVersion}-article-container advertory-container-anchor`}
      >
        <ArticleType article={article} commonProps={commonProps} />
        <DataController />
      </div>
    </ListsProvider>
  );
};

const ArticlePageWithPulse = withByfunnPulse(ArticlePage);

export { ArticlePageWithPulse as ArticlePage };
